import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic } from "lucide-react";
import { toast } from "sonner";
import { matchVoiceRoute, VOICE_ROUTES } from "@/lib/voice/voiceNavigation";

const HOTKEY_HINT = "Ctrl/⌘ + Shift + J";

type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
};

const getRecognition = (): Recognition | null => {
  const Ctor =
    (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  if (!Ctor) return null;
  const instance: Recognition = new Ctor();
  instance.lang = document.documentElement.lang || "en-US";
  instance.continuous = false;
  instance.interimResults = false;
  return instance;
};

/**
 * Wave 8 — Voice navigation. Press the hotkey, say "open batch runs", and the
 * app routes there. Nothing listens until the user explicitly triggers it.
 */
export const VoiceNavigationListener = () => {
  const navigate = useNavigate();
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<Recognition | null>(null);

  const stop = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      /* already stopped */
    }
    recognitionRef.current = null;
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (recognitionRef.current) {
      stop();
      return;
    }
    const recognition = getRecognition();
    if (!recognition) {
      toast.error("Voice navigation isn't supported in this browser.");
      return;
    }
    recognitionRef.current = recognition;
    setListening(true);

    recognition.onresult = (event: any) => {
      const spoken: string = event?.results?.[0]?.[0]?.transcript ?? "";
      const match = matchVoiceRoute(spoken);
      if (match) {
        toast.success(`Opening ${match.label}`);
        navigate(match.route);
      } else {
        toast.message("Didn't catch a destination", {
          description: `Heard "${spoken.slice(0, 60)}". Try "open dashboard" or "go to tools".`,
        });
      }
    };
    recognition.onerror = () => {
      toast.error("Couldn't hear you. Check microphone permissions.");
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      setListening(false);
    };

    try {
      recognition.start();
    } catch {
      stop();
    }
  }, [navigate, stop]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "j") {
        event.preventDefault();
        start();
      }
      if (event.key === "Escape" && recognitionRef.current) stop();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      stop();
    };
  }, [start, stop]);

  return (
    <>
      <span className="sr-only" aria-live="polite">
        {listening ? "Listening for a destination" : ""}
      </span>
      {listening && (
        <div
          role="status"
          className="pointer-events-none fixed bottom-24 left-1/2 z-[70] -translate-x-1/2 rounded-full border border-border bg-background/95 px-4 py-2 text-sm shadow-lg"
        >
          <span className="flex items-center gap-2">
            <Mic className="h-4 w-4 animate-pulse text-primary" aria-hidden="true" />
            Listening… say “open {VOICE_ROUTES[0].label.toLowerCase()}”
          </span>
        </div>
      )}
      <span className="sr-only">Voice navigation hotkey {HOTKEY_HINT}</span>
    </>
  );
};
